import React, { useEffect, useState, useRef } from "react";
import DataTable from 'react-data-table-component';
import axios from "axios";
import {
    FiSearch,
    FiFileText,
    FiFile,
    FiRefreshCw,
    FiDownload
} from "react-icons/fi";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from "jspdf-autotable";
import { Button } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState("");
    const navigate = useNavigate();
    const hasFetched = useRef(false);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const userRes = await axios.get("https://www.azzenterprises.com/api/admin/getuser", {
                withCredentials: true
            });

            const user = userRes.data;
            setUserRole(user.user_role || "viewer");
            localStorage.setItem("can_access", JSON.stringify(user.can_access || 0));
            localStorage.setItem("user_name", user.user_name || "");

            await fetchCustomersData();
        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            setError(serverMsg);

            if (serverMsg === "Token required") {
                if (!toast.isActive("token-expired")) {
                    toast.error("Session expired. Redirecting to login...", { toastId: "token-expired" });
                }
                setTimeout(() => navigate("/login"), 1300);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchCustomersData = async () => {
        try {
            setLoading(true);
            const userName = localStorage.getItem("user_name") || "";
            const canAccess = JSON.parse(localStorage.getItem("can_access")) || 0;

            let customersRes;
            if (canAccess === 1) {
                customersRes = await axios.get("https://www.azzenterprises.com/api/customers", {
                    withCredentials: true
                });
            } else {
                customersRes = await axios.get(`https://www.azzenterprises.com/api/customers/bystaff/${userName}`, {
                    withCredentials: true
                });
            }

            setCustomers(customersRes.data);
            setFilteredCustomers(customersRes.data);
            setError(null);
        } catch (err) {
            const serverMsg = err.response?.data?.error || err.message;
            setError(serverMsg);
            toast.error(`Failed to fetch customers: ${serverMsg}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            fetchAllData();
            hasFetched.current = true;
        }
    }, []);

    useEffect(() => {
        const filtered = customers.filter(c =>
            c.full_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCustomers(filtered);
    }, [searchTerm, customers]);

    const formatDate = (dateStr) =>
        dateStr
            ? new Date(dateStr).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
            })
            : "-";

    const formatCurrency = (amount) =>
        amount ? `$${parseFloat(amount).toFixed(2)}` : "-";

    const prepareExportData = () =>
        filteredCustomers.map((c) => ({
            "Customer ID": c.customer_id,
            "Full Name": c.full_name,
            "Email": c.email_address,
            "Phone": c.phone_number,
            "DOB": formatDate(c.date_of_birth),
            "ID Type": c.id_type,
            "ID Number": c.id_number,
            "Passport": c.passport_number || "-",
            "Bill No": c.bill_number,
            "Bill Amt": formatCurrency(c.bill_amount),
            "Product": c.product_name,
            "EMI Plan": c.emi_plan_duration,
            "Status": c.status,
            "Bank Code": c.applicantBankCode,
            "DDA Ref": c.boDDARefNo,
            "BO Name": c.boName,
            "BO Tx Ref": c.boTransactionRefNo,
            "Client ID": c.clientID,
            "Purpose": c.purpose,
            "Cancel flag": c.cancelFlag,
            "Req ID": c.requestID,
            "Req Type": c.requestType,
            "Segment": c.segment,
            "Timestamp": formatDate(c.timestamp),
        }));

    const handleExportCSV = () => {
        const data = prepareExportData();
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
        XLSX.writeFile(workbook, "customers_data.csv", { bookType: "csv" });
    };

    const handleExportExcel = () => {
        const data = prepareExportData();
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");
        XLSX.writeFile(workbook, "customers_data.xlsx");
    };

    const handleExportPDF = () => {
        if (filteredCustomers.length === 0) return alert("No data to export");

        const data = prepareExportData();
        const headers = [Object.keys(data[0])];
        const body = data.map(item => Object.values(item));

        const doc = new jsPDF({ orientation: "landscape", unit: "mm" });
        doc.setFontSize(16);
        doc.text("Customers Report", 14, 15);

        autoTable(doc, {
            head: headers,
            body: body,
            startY: 25,
            styles: { fontSize: 7, cellPadding: 1, overflow: 'linebreak' },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
            margin: { top: 20 },
            tableWidth: 'wrap'
        });

        doc.save("customers_report.pdf");
    };

    const columns = [
        { name: "ID", selector: row => row.customer_id, sortable: true },
        { name: "Name", selector: row => row.full_name, sortable: true },
        { name: "Staff", selector: row => row.staff_name, sortable: true },
        { name: "Email", selector: row => row.email_address, sortable: true },
        { name: "Phone", selector: row => row.phone_number, sortable: true },
        { name: "DOB", selector: row => formatDate(row.date_of_birth), sortable: true },
        { name: "ID Type", selector: row => row.id_type, sortable: true },
        { name: "ID Number", selector: row => row.id_number, sortable: true },
        { name: "Passport", selector: row => row.passport_number || "-", sortable: true },
        { name: "Bill No", selector: row => row.bill_number, sortable: true },
        { name: "Bill Amt", selector: row => formatCurrency(row.bill_amount), sortable: true },
        { name: "Product", selector: row => row.product_name, sortable: true },
        { name: "EMI Plan", selector: row => row.emi_plan_duration, sortable: true },
        // { name: "Status", selector: row => row.status, sortable: true },
        { name: "Bank Code", selector: row => row.applicantBankCode, sortable: true },
        { name: "DDA Ref", selector: row => row.boDDARefNo, sortable: true },
        { name: "BO Name", selector: row => row.boName, sortable: true },
        { name: "BO Tx Ref", selector: row => row.boTransactionRefNo, sortable: true },
        { name: "Client ID", selector: row => row.clientID, sortable: true },
        { name: "Purpose", selector: row => row.purpose, sortable: true },
        { name: "Cancel flag", selector: row => row.cancelFlag, sortable: true },
        { name: "Req ID", selector: row => row.requestID, sortable: true },
        { name: "Req Type", selector: row => row.requestType, sortable: true },
        { name: "Segment", selector: row => row.segment, sortable: true },
        { name: "Timestamp", selector: row => formatDate(row.timestamp), sortable: true },
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mt-4">
                <h5 className="alert-heading">Error loading customers</h5>
                <p>{error}</p>
                <button className="btn btn-outline-danger" onClick={() => window.location.reload()}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <ToastContainer position="top-right" autoClose={1000} />
            <div className="card">
                <div className="card-body">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                        <div className="input-group" style={{ maxWidth: "300px" }}>
                            <span className="input-group-text"><FiSearch /></span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {userRole !== "viewer" && (
                            <div className="d-flex gap-2 flex-wrap">
                                <Button variant="outline-secondary" size="sm" onClick={fetchCustomersData}>
                                    <FiRefreshCw /> Refresh
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={handleExportCSV}>
                                    <FiFileText /> CSV
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={handleExportExcel}>
                                    <FiFile /> Excel
                                </Button>
                                <Button variant="outline-success" size="sm" onClick={handleExportPDF}>
                                    <FiDownload /> PDF
                                </Button>
                            </div>
                        )}
                    </div>

                    <DataTable
                        columns={columns}
                        data={filteredCustomers}
                        pagination
                        highlightOnHover
                        responsive
                        striped
                        persistTableHead
                        fixedHeader
                        fixedHeaderScrollHeight="600px"
                        defaultSortFieldId={1}
                        customStyles={{
                            headCells: { style: { backgroundColor: "#f8f9fa", fontWeight: "bold" } },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Customers;
